import React, { useState } from 'react';
import SpotifyPlayer from './SpotifyPlayer';
import 'bulma/css/bulma.min.css';
import './Tunes.css';

function Tunes() {
  // Main view state management
  const [currentView, setCurrentView] = useState('search');
  
  // Search form data state
  const [searchFormData, setSearchFormData] = useState({
    trackName: '',
    artist: '',
    genre: '',
    downloadOnly: false,
    bpmRange: '',
    musicalKey: '',
    energyLevel: '',
    releaseYear: ''
  });
  
  // Advanced search visibility
  const [showAdvanced, setShowAdvanced] = useState(false);
  
  // Filter and sort state
  const [sortBy, setSortBy] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showMoreInfo, setShowMoreInfo] = useState(false);
  const [selectedTrack, setSelectedTrack] = useState(null);
  
  // Enhanced music database with realistic electronic music
  const [musicDatabase] = useState([
    { id: 1, title: 'Digital Dreams', artist: 'Synth Wave', genre: 'House', bpm: 128, key: 'C Major', energy: 8, duration: '4:32', downloadable: true, releaseDate: '2024-01-15', description: 'Uplifting house track with ethereal synths and driving bassline' },
    { id: 2, title: 'Midnight Pulse', artist: 'Electro Beats', genre: 'Techno', bpm: 140, key: 'A Minor', energy: 9, duration: '5:15', downloadable: true, releaseDate: '2024-02-20', description: 'Dark techno with industrial sounds and relentless rhythm' },
    { id: 3, title: 'Urban Flow', artist: 'Street Sounds', genre: 'Hip-Hop', bpm: 95, key: 'F Major', energy: 6, duration: '3:48', downloadable: false, releaseDate: '2023-12-10', description: 'Smooth hip-hop with jazzy samples and laid-back vibe' },
    { id: 4, title: 'Bass Drop', artist: 'Sub Bass', genre: 'Dubstep', bpm: 150, key: 'G Minor', energy: 10, duration: '4:02', downloadable: true, releaseDate: '2024-03-05', description: 'Heavy dubstep with massive bass drops and aggressive synths' },
    { id: 5, title: 'Thunder Strike', artist: 'DJ Electro', genre: 'House', bpm: 128, key: 'D Major', energy: 8, duration: '4:56', downloadable: true, releaseDate: '2024-01-30', description: 'Progressive house with orchestral elements and epic breakdowns' },
    { id: 6, title: 'Neon Lights', artist: 'Cyber DJ', genre: 'Electronic', bpm: 135, key: 'E Minor', energy: 7, duration: '3:24', downloadable: false, releaseDate: '2023-11-25', description: 'Futuristic electronic with cyberpunk aesthetics and neon vibes' },
    { id: 7, title: 'Deep Space', artist: 'Cosmic Beats', genre: 'Ambient', bpm: 90, key: 'B Major', energy: 4, duration: '6:18', downloadable: true, releaseDate: '2024-02-14', description: 'Atmospheric ambient with spacey pads and floating melodies' },
    { id: 8, title: 'Electric Storm', artist: 'Lightning DJ', genre: 'Trance', bpm: 132, key: 'F# Minor', energy: 9, duration: '5:42', downloadable: true, releaseDate: '2024-03-12', description: 'Epic trance with soaring leads and emotional breakdowns' },
    { id: 9, title: 'Underground', artist: 'Dark Bass', genre: 'Drum & Bass', bpm: 174, key: 'C Minor', energy: 10, duration: '4:15', downloadable: false, releaseDate: '2023-10-08', description: 'Fast-paced drum & bass with complex rhythms and dark atmosphere' },
    { id: 10, title: 'Sunset Vibes', artist: 'Chill Master', genre: 'Deep House', bpm: 120, key: 'G Major', energy: 5, duration: '7:30', downloadable: true, releaseDate: '2024-01-08', description: 'Chill deep house perfect for sunset sessions and relaxation' }
  ]);

  // Sample featured tracks data
  const featuredTracks = [
    { id: 1, title: 'Thunder Strike', artist: 'DJ Electro', bpm: 128, genre: 'House' },
    { id: 2, title: 'Midnight Vibes', artist: 'Beat Master', bpm: 140, genre: 'Techno' },
    { id: 3, title: 'Urban Flow', artist: 'Street Sounds', bpm: 95, genre: 'Hip-Hop' },
    { id: 4, title: 'Digital Dreams', artist: 'Synth Wave', bpm: 120, genre: 'Electronic' },
    { id: 5, title: 'Bass Drop', artist: 'Sub Bass', bpm: 150, genre: 'Dubstep' }
  ];

  // Form input handlers
  const handleInputChange = (field, value) => {
    setSearchFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Search functionality
  const handleSearch = (e) => {
    e.preventDefault();
    
    // Filter music database based on search criteria
    let filteredResults = musicDatabase.filter(track => {
      const matchesTrackName = !searchFormData.trackName || 
        track.title.toLowerCase().includes(searchFormData.trackName.toLowerCase());
      const matchesArtist = !searchFormData.artist || 
        track.artist.toLowerCase().includes(searchFormData.artist.toLowerCase());
      const matchesGenre = !searchFormData.genre || 
        track.genre.toLowerCase().includes(searchFormData.genre.toLowerCase());
      const matchesDownload = !searchFormData.downloadOnly || track.downloadable;
      
      // Advanced filters
      const matchesBpmRange = !searchFormData.bpmRange || 
        checkBpmRange(track.bpm, searchFormData.bpmRange);
      const matchesKey = !searchFormData.musicalKey || 
        track.key.toLowerCase().includes(searchFormData.musicalKey.toLowerCase());
      const matchesEnergy = !searchFormData.energyLevel || 
        checkEnergyLevel(track.energy, searchFormData.energyLevel);
      const matchesYear = !searchFormData.releaseYear || 
        checkReleaseYear(track.releaseDate, searchFormData.releaseYear);
      
      return matchesTrackName && matchesArtist && matchesGenre && matchesDownload &&
             matchesBpmRange && matchesKey && matchesEnergy && matchesYear;
    });
    
    // Apply sorting
    if (sortBy) {
      filteredResults = sortResults(filteredResults, sortBy);
    }
    
    setSearchResults(filteredResults);
    setCurrentView('results');
  };

  // Helper functions for filtering
  const checkBpmRange = (trackBpm, bpmRange) => {
    if (!bpmRange) return true;
    const [min, max] = bpmRange.split('-').map(Number);
    return trackBpm >= min && trackBpm <= max;
  };

  const checkEnergyLevel = (trackEnergy, energyLevel) => {
    if (!energyLevel) return true;
    const [min, max] = energyLevel.split('-').map(Number);
    return trackEnergy >= min && trackEnergy <= max;
  };

  const checkReleaseYear = (releaseDate, yearRange) => {
    if (!yearRange) return true;
    const trackYear = new Date(releaseDate).getFullYear();
    const [min, max] = yearRange.split('-').map(Number);
    return trackYear >= min && trackYear <= max;
  };

  // Sorting functionality
  const sortResults = (results, sortOption) => {
    const sortedResults = [...results];
    
    switch (sortOption) {
      case 'bpm-low':
        return sortedResults.sort((a, b) => a.bpm - b.bpm);
      case 'bpm-high':
        return sortedResults.sort((a, b) => b.bpm - a.bpm);
      case 'alphabetical':
        return sortedResults.sort((a, b) => a.title.localeCompare(b.title));
      case 'energy':
        return sortedResults.sort((a, b) => b.energy - a.energy);
      case 'newest':
        return sortedResults.sort((a, b) => new Date(b.releaseDate) - new Date(a.releaseDate));
      default:
        return sortedResults;
    }
  };

  // Navigation functions
  const handleBackToSearch = () => {
    setCurrentView('search');
    setSearchResults([]);
    setSortBy('');
    setShowAdvanced(false);
    // Reset form data
    setSearchFormData({
      trackName: '',
      artist: '',
      genre: '',
      downloadOnly: false,
      bpmRange: '',
      musicalKey: '',
      energyLevel: '',
      releaseYear: ''
    });
  };

  // Toggle advanced search
  const toggleAdvanced = () => {
    setShowAdvanced(!showAdvanced);
  };

  // Handle sort change
  const handleSortChange = (sortOption) => {
    setSortBy(sortOption);
    if (searchResults.length > 0) {
      const sortedResults = sortResults(searchResults, sortOption);
      setSearchResults(sortedResults);
    }
  };

  // More Info functionality
  const toggleMoreInfo = (track) => {
    setSelectedTrack(track);
    setShowMoreInfo(!showMoreInfo);
  };

  // Track action handlers
  const handlePlayTrack = (track) => {
    console.log('Playing track:', track.title);
    // TODO: Implement actual play functionality
  };

  const handleDownloadTrack = (track) => {
    console.log('Downloading track:', track.title);
    // TODO: Implement actual download functionality
  };

  return (
    <div className="has-background-black has-text-white">
      {/* Header Section */}
      <header className="hero is-primary">
        <div className="hero-body">
          <div className="container">
            <div className="level">
              <div className="level-left">
                <div className="level-item">
                  <div>
                    <h1 className="title is-1 has-text-white">Music DJ Database</h1>
                    <h2 className="subtitle is-4 has-text-white">Season 00 (inception) MTB - 2025</h2>
                    <p className="has-text-white">Muay Thai Beats Inc.</p>
                  </div>
                </div>
              </div>
              <div className="level-right">
                <div className="level-item">
                  <a 
                    href="https://dj.muaythaibeats.com"
                    className="button is-white is-outlined is-large"
                  >
                    DJ Login
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Search Form Section */}
      <section className="section has-background-light">
        <div className="container">
          <div className="columns is-centered">
            <div className="column is-two-thirds">
              <div className="box">
                <h3 className="title is-3 has-text-centered mb-5">Music Database Search</h3>
                
                <form onSubmit={handleSearch}>
                  <div className="columns">
                    <div className="column">
                      <div className="field">
                        <label className="label">Track Name</label>
                        <div className="control">
                          <input
                            className="input"
                            type="text"
                            placeholder="Enter track name..."
                            value={searchFormData.trackName}
                            onChange={(e) => handleInputChange('trackName', e.target.value)}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="column">
                      <div className="field">
                        <label className="label">Artist</label>
                        <div className="control">
                          <input
                            className="input"
                            type="text"
                            placeholder="Enter artist name..."
                            value={searchFormData.artist}
                            onChange={(e) => handleInputChange('artist', e.target.value)}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="column">
                      <div className="field">
                        <label className="label">Genre</label>
                        <div className="control">
                          <input
                            className="input"
                            type="text"
                            placeholder="Enter genre..."
                            value={searchFormData.genre}
                            onChange={(e) => handleInputChange('genre', e.target.value)}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="field">
                    <div className="control">
                      <label className="checkbox">
                        <input
                          type="checkbox"
                          checked={searchFormData.downloadOnly}
                          onChange={(e) => handleInputChange('downloadOnly', e.target.checked)}
                        />
                        {' '}Only show tracks available for download
                      </label>
                    </div>
                  </div>

                  {/* Advanced Search Toggle */}
                  <div className="field">
                    <div className="control has-text-centered">
                      <button 
                        type="button"
                        className="button is-link is-outlined"
                        onClick={toggleAdvanced}
                      >
                        {showAdvanced ? 'Hide' : 'Show'} Advanced Search
                      </button>
                    </div>
                  </div>

                  {/* Advanced Search Section */}
                  {showAdvanced && (
                    <div className="box has-background-light">
                      <h4 className="title is-5 has-text-centered mb-4">Advanced Search Options</h4>
                      
                      <div className="columns is-multiline">
                        <div className="column is-half">
                          <div className="field">
                            <label className="label">BPM Range</label>
                            <div className="control">
                              <input
                                className="input"
                                type="text"
                                placeholder="120-140"
                                value={searchFormData.bpmRange}
                                onChange={(e) => handleInputChange('bpmRange', e.target.value)}
                              />
                            </div>
                          </div>
                        </div>
                        
                        <div className="column is-half">
                          <div className="field">
                            <label className="label">Musical Key</label>
                            <div className="control">
                              <input
                                className="input"
                                type="text"
                                placeholder="C Major, A Minor, etc..."
                                value={searchFormData.musicalKey}
                                onChange={(e) => handleInputChange('musicalKey', e.target.value)}
                              />
                            </div>
                          </div>
                        </div>
                        
                        <div className="column is-half">
                          <div className="field">
                            <label className="label">Energy Level</label>
                            <div className="control">
                              <input
                                className="input"
                                type="text"
                                placeholder="1-10 scale"
                                value={searchFormData.energyLevel}
                                onChange={(e) => handleInputChange('energyLevel', e.target.value)}
                              />
                            </div>
                          </div>
                        </div>
                        
                        <div className="column is-half">
                          <div className="field">
                            <label className="label">Release Year</label>
                            <div className="control">
                              <input
                                className="input"
                                type="text"
                                placeholder="2020-2024"
                                value={searchFormData.releaseYear}
                                onChange={(e) => handleInputChange('releaseYear', e.target.value)}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div className="field">
                    <div className="control has-text-centered">
                      <button 
                        className="button is-primary is-large is-fullwidth"
                        type="submit"
                      >
                        Search Music Database
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Tracks Section */}
      <section className="section has-background-light">
        <div className="container">
          <h3 className="title is-3 has-text-centered mb-5">Featured Tracks</h3>
          
          {/* Now Playing / Featured Track */}
          <div className="columns is-centered mb-6">
            <div className="column is-half">
              <div className="box">
                <h4 className="title is-4 has-text-centered mb-4">Now Playing / Featured Track</h4>
                <SpotifyPlayer />
              </div>
            </div>
          </div>

          {/* Featured Track Cards */}
          <div className="featured-tracks-container">
            <div className="columns is-multiline">
              {featuredTracks.map((track) => (
                <div key={track.id} className="column is-one-fifth">
                <div className="card">
                  <div className="card-content">
                    <div className="media">
                      <div className="media-content">
                        <p className="title is-6">{track.title}</p>
                        <p className="subtitle is-7">{track.artist}</p>
                      </div>
                    </div>
                    <div className="content">
                      <div className="tags">
                        <span className="tag is-primary">{track.bpm} BPM</span>
                        <span className="tag is-light">{track.genre}</span>
                      </div>
                    </div>
                    <footer className="card-footer">
                      <button 
                        onClick={() => handlePlayTrack(track)} 
                        className="card-footer-item button is-white is-borderless"
                      >
                        <span className="icon">
                          <i className="fas fa-play"></i>
                        </span>
                        Play
                      </button>
                      <button 
                        onClick={() => handleDownloadTrack(track)}
                        className="card-footer-item button is-white is-borderless"
                      >
                        <span className="icon">
                          <i className="fas fa-download"></i>
                        </span>
                        Download
                      </button>
                    </footer>
                  </div>
                </div>
              </div>
            ))}
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div className="container is-fluid py-6">
        {currentView === 'search' ? (
          <div className="columns is-centered">
            <div className="column is-half">
              <div className="box has-background-dark">
                <p className="has-text-white has-text-centered">Use the search form above to find tracks in our database</p>
              </div>
            </div>
          </div>
        ) : (
          <div className="columns">
            {/* Sidebar Filter Panel */}
            <div className="column is-one-quarter">
              <aside className="menu has-background-primary has-text-white">
                <p className="menu-label has-text-white">
                  <span className="icon">
                    <i className="fas fa-filter"></i>
                  </span>
                  Sort & Filter
                </p>
                <ul className="menu-list">
                  <li>
                    <div className="field">
                      <label className="label has-text-white">Sort By</label>
                      <div className="control">
                        <label className="radio has-text-white">
                          <input
                            type="radio"
                            name="sortBy"
                            value="bpm-low"
                            checked={sortBy === 'bpm-low'}
                            onChange={(e) => handleSortChange(e.target.value)}
                          />
                          {' '}BPM: Low to High
                        </label>
                      </div>
                      <div className="control">
                        <label className="radio has-text-white">
                          <input
                            type="radio"
                            name="sortBy"
                            value="bpm-high"
                            checked={sortBy === 'bpm-high'}
                            onChange={(e) => handleSortChange(e.target.value)}
                          />
                          {' '}BPM: High to Low
                        </label>
                      </div>
                      <div className="control">
                        <label className="radio has-text-white">
                          <input
                            type="radio"
                            name="sortBy"
                            value="alphabetical"
                            checked={sortBy === 'alphabetical'}
                            onChange={(e) => handleSortChange(e.target.value)}
                          />
                          {' '}Alphabetical Order
                        </label>
                      </div>
                      <div className="control">
                        <label className="radio has-text-white">
                          <input
                            type="radio"
                            name="sortBy"
                            value="energy"
                            checked={sortBy === 'energy'}
                            onChange={(e) => handleSortChange(e.target.value)}
                          />
                          {' '}Energy Level
                        </label>
                      </div>
                      <div className="control">
                        <label className="radio has-text-white">
                          <input
                            type="radio"
                            name="sortBy"
                            value="newest"
                            checked={sortBy === 'newest'}
                            onChange={(e) => handleSortChange(e.target.value)}
                          />
                          {' '}Newest Tracks
                        </label>
                      </div>
                    </div>
                  </li>
                </ul>
              </aside>
            </div>

            {/* Main Results Content */}
            <div className="column is-three-quarters">
              <div className="box">
                <div className="level">
                  <div className="level-left">
                    <h3 className="title is-3">Search Results</h3>
                  </div>
                  <div className="level-right">
                    <button 
                      className="button is-primary"
                      onClick={handleBackToSearch}
                    >
                      <span className="icon">
                        <i className="fas fa-arrow-left"></i>
                      </span>
                      <span>Back to Search</span>
                    </button>
                  </div>
                </div>
                
                <div className="content">
                  <p className="has-text-grey">Found {searchResults.length} results</p>
                  
                  <div className="table-container">
                    <table className="table is-fullwidth is-striped is-hoverable">
                      <thead>
                        <tr>
                          <th>Track Name</th>
                          <th>Artist</th>
                          <th>Genre</th>
                          <th>BPM</th>
                          <th>Key</th>
                          <th>Energy</th>
                          <th>Duration</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {searchResults.map((result) => (
                          <tr key={result.id}>
                            <td className="has-text-weight-semibold">{result.title}</td>
                            <td>{result.artist}</td>
                            <td>
                              <span className="tag is-light">{result.genre}</span>
                            </td>
                            <td>
                              <span className="tag is-primary">{result.bpm}</span>
                            </td>
                            <td>{result.key}</td>
                            <td>
                              <span className="tag is-info">{result.energy}/10</span>
                            </td>
                            <td>{result.duration}</td>
                            <td>
                              <div className="buttons">
                                <button 
                                  className="button is-small is-primary"
                                  onClick={() => handlePlayTrack(result)}
                                  title="Play Track"
                                >
                                  <span className="icon">
                                    <i className="fas fa-play"></i>
                                  </span>
                                </button>
                                <button 
                                  className="button is-small is-success"
                                  onClick={() => handleDownloadTrack(result)}
                                  disabled={!result.downloadable}
                                  title={result.downloadable ? "Download Track" : "Not Available for Download"}
                                >
                                  <span className="icon">
                                    <i className="fas fa-download"></i>
                                  </span>
                                </button>
                                <button 
                                  className="button is-small is-info"
                                  onClick={() => toggleMoreInfo(result)}
                                  title="More Info"
                                >
                                  <span className="icon">
                                    <i className="fas fa-info"></i>
                                  </span>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* More Info Section */}
                {showMoreInfo && selectedTrack && (
                  <div className="more-info-section">
                    <div className="level">
                      <div className="level-left">
                        <h4 className="title is-4">Track Details</h4>
                      </div>
                      <div className="level-right">
                        <button 
                          className="button more-info-toggle"
                          onClick={() => setShowMoreInfo(false)}
                        >
                          <span className="icon">
                            <i className="fas fa-times"></i>
                          </span>
                          <span>Close</span>
                        </button>
                      </div>
                    </div>
                    
                    <div className="more-info-content">
                      <div className="columns">
                        <div className="column is-half">
                          <h5 className="title is-5">{selectedTrack.title}</h5>
                          <p className="subtitle is-6">by {selectedTrack.artist}</p>
                          <p className="content">{selectedTrack.description}</p>
                        </div>
                        <div className="column is-half">
                          <div className="tags">
                            <span className="tag is-primary">{selectedTrack.bpm} BPM</span>
                            <span className="tag is-info">{selectedTrack.energy}/10 Energy</span>
                            <span className="tag is-light">{selectedTrack.genre}</span>
                            <span className="tag is-warning">{selectedTrack.key}</span>
                          </div>
                          <div className="content mt-4">
                            <p><strong>Duration:</strong> {selectedTrack.duration}</p>
                            <p><strong>Release Date:</strong> {new Date(selectedTrack.releaseDate).toLocaleDateString()}</p>
                            <p><strong>Download Status:</strong> 
                              <span className={`tag ml-2 ${selectedTrack.downloadable ? 'is-success' : 'is-danger'}`}>
                                {selectedTrack.downloadable ? 'Available' : 'Not Available'}
                              </span>
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


export default Tunes;